import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Recomputes genuine incidents over a fixed period: the one way a day older
 * than the scheduled run's two is computed again, so reported numbers change
 * only when someone asks. Support-only, behind `monitoring_incident_recompute`,
 * which the server checks too.
 *
 * Declared here rather than imported from the batch so the wire contract is
 * this module's, per docs/api.practices.md.
 */

const HALF_HOUR_MS = 30 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** The longest period one recompute takes, as the batch allows: a year and a day. */
export const INCIDENTS_RECOMPUTE_MAX_DAYS = 367;

/** An instant in any offset that falls on a half-hour in UTC, where incidents are counted. */
const HalfHourInstant = z.iso
  .datetime({ offset: true })
  .refine((at) => Date.parse(at) % HALF_HOUR_MS === 0, "Must fall on the hour or half-hour in UTC");

export const StartIncidentsRecomputeRequest = z
  .object({
    since: HalfHourInstant,
    /** Exclusive. Must have passed: a period still running would store a partial day as whole. */
    until: HalfHourInstant,
  })
  .refine((request) => Date.parse(request.since) < Date.parse(request.until), {
    message: "The period must end after it starts",
    path: ["until"],
  })
  .refine((request) => Date.parse(request.until) - Date.parse(request.since) <= INCIDENTS_RECOMPUTE_MAX_DAYS * DAY_MS, {
    message: `The period can be at most ${String(INCIDENTS_RECOMPUTE_MAX_DAYS)} days`,
    path: ["until"],
  });
export type StartIncidentsRecomputeRequest = z.output<typeof StartIncidentsRecomputeRequest>;

export const StartIncidentsRecomputeResponse = z.object({
  /** When the API handed the period to the batch. The run is recorded moments later. */
  startedAt: z.iso.datetime(),
  recompute: StartIncidentsRecomputeRequest,
});
export type StartIncidentsRecomputeResponse = z.output<typeof StartIncidentsRecomputeResponse>;

/** Under `commands/`: it rewrites stored incidents. The path's tenant plays no part. */
export const incidentsRecomputeResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/commands/incidents-recompute",
  scope: "tenant",
});

/**
 * Answers as soon as the batch has the period, not when it is done. Its
 * progress shows in the run's `chunks`.
 */
export const startIncidentsRecomputeEndpoint = defineEndpoint(incidentsRecomputeResource, {
  method: "POST",
  bodySchema: StartIncidentsRecomputeRequest,
  responseSchema: StartIncidentsRecomputeResponse,
});

/** The server pins this to the batch's own list. */
export const INCIDENTS_RECOMPUTE_CHUNK_STATUSES = ["pending", "running", "done", "failed"] as const;
export type IncidentsRecomputeChunkStatus = (typeof INCIDENTS_RECOMPUTE_CHUNK_STATUSES)[number];

/** One UTC day of a recompute, or the part of one its period covers. */
export const IncidentsRecomputeChunk = z.object({
  since: z.string(),
  until: z.string(),
  /** `running` also covers an attempt that died; the next one retries it. */
  status: z.enum(INCIDENTS_RECOMPUTE_CHUNK_STATUSES),
  /** Attempts started. Above one, earlier ones failed and `lastError` says why. */
  attempts: z.int().nonnegative(),
  lastError: z.string().optional(),
  startedAt: z.string().optional(),
  finishedAt: z.string().optional(),
  /** What it read from the replica and counted, once done. Reads include the ±6 hours around the day. */
  activationsRead: z.int().nonnegative().optional(),
  operatorNotesRead: z.int().nonnegative().optional(),
  genuineIncidents: z.int().nonnegative().optional(),
  nonGenuineActivations: z.int().nonnegative().optional(),
});
export type IncidentsRecomputeChunk = z.output<typeof IncidentsRecomputeChunk>;

/** One recompute from its record: who asked, for which period, when, and how each day went. */
export const IncidentsRecomputeRun = z.object({
  runId: z.string(),
  /** Absent for a recompute started outside Patriot tools. */
  requestedBy: z.string().optional(),
  /** The requester's address, where it could be found. */
  requestedByEmail: z.string().optional(),
  since: z.string(),
  until: z.string(),
  startedAt: z.string(),
  /** In time order. */
  chunks: z.array(IncidentsRecomputeChunk),
});
export type IncidentsRecomputeRun = z.output<typeof IncidentsRecomputeRun>;

export const ListIncidentsRecomputesQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
  cursor: z.string().optional(),
});
export type ListIncidentsRecomputesQueryParamsWire = z.input<typeof ListIncidentsRecomputesQueryParams>;
export type ListIncidentsRecomputesQueryParams = z.output<typeof ListIncidentsRecomputesQueryParams>;

export const ListIncidentsRecomputesResponse = z.object({
  /** Newest first. */
  items: z.array(IncidentsRecomputeRun),
  /** Opaque resume token; `null` when there are no older runs. */
  cursor: z.string().nullable(),
});
export type ListIncidentsRecomputesResponse = z.output<typeof ListIncidentsRecomputesResponse>;

/** Fleet-wide like the backfills: the path's tenant plays no part. */
export const incidentsRecomputesResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/incidents-recomputes",
  scope: "tenant",
});

export const listIncidentsRecomputesEndpoint = defineEndpoint(incidentsRecomputesResource, {
  responseSchema: ListIncidentsRecomputesResponse,
  querySchema: ListIncidentsRecomputesQueryParams,
});
